import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowDownLeft, ArrowUpRight, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Card, CardContent, CardHeader } from '@/components/ui';
import { kgApi, type EntityNeighbour } from '@/lib/api';
import { cn } from '@/lib/cn';
import { useApiError } from '@/lib/i18n/useApiError';

interface DetailPanelProps {
  iri: string | null;
  onNeighbourClick: (iri: string) => void;
}

export function DetailPanel({ iri, onNeighbourClick }: DetailPanelProps) {
  const { t } = useTranslation(['studio', 'common']);
  const apiError = useApiError();
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['explorer', 'detail', iri],
    queryFn: () => kgApi.entityDetail(iri as string),
    enabled: !!iri,
    staleTime: 60_000,
    retry: false,
  });

  if (!iri) {
    return (
      <Card className="h-full">
        <CardContent className="flex h-full items-center justify-center p-6 text-small text-slate-500">
          {t('studio:explorer.selectNode')}
        </CardContent>
      </Card>
    );
  }
  if (isLoading) {
    return (
      <Card className="h-full">
        <CardContent className="flex h-full items-center justify-center p-6 text-small text-slate-500">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
          {t('common:status.loading')}
        </CardContent>
      </Card>
    );
  }
  if (isError || !data) {
    return (
      <Card className="h-full">
        <CardContent className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-small text-amber-700">
          <AlertTriangle className="h-5 w-5" aria-hidden />
          <p>{apiError(error) ?? t('common:status.error')}</p>
          <p className="break-all font-mono text-small text-slate-400">{iri}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="flex h-full flex-col overflow-hidden">
      <CardHeader className="border-b border-slate-200">
        <h2 className="text-h2 text-brand-900">{data.label}</h2>
        <p className="font-mono text-small text-slate-500">{data.class_name}</p>
        <p className="break-all font-mono text-small text-slate-400" title={data.iri}>
          {data.iri}
        </p>
        {data.types.length > 1 && (
          <p className="mt-1 text-small text-slate-500">
            {t('studio:explorer.allTypes')}:{' '}
            {data.types.map((t) => localName(t)).join(', ')}
          </p>
        )}
      </CardHeader>
      <CardContent className="flex-1 space-y-4 overflow-auto p-4 text-small">
        {data.literals.length > 0 && (
          <Section title={t('studio:explorer.literals', { count: data.literals.length })}>
            <ul className="space-y-1">
              {data.literals.map((l, idx) => (
                <li key={`${l.predicate_iri}-${idx}`} className="grid grid-cols-[1fr_2fr] gap-2">
                  <span className="font-mono text-slate-500" title={l.predicate_iri}>
                    {l.predicate}
                  </span>
                  <span className="break-all text-slate-800">
                    {l.value}
                    {l.datatype && (
                      <span className="ml-1 text-small text-slate-400">
                        ({l.datatype})
                      </span>
                    )}
                    {l.lang && (
                      <span className="ml-1 text-small text-slate-400">@{l.lang}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </Section>
        )}
        {data.outgoing.length > 0 && (
          <Section
            title={t('studio:explorer.outgoing', { count: data.outgoing.length })}
            icon={<ArrowUpRight className="h-3.5 w-3.5 text-emerald-600" aria-hidden />}
          >
            <NeighbourList items={data.outgoing} onClick={onNeighbourClick} />
          </Section>
        )}
        {data.incoming.length > 0 && (
          <Section
            title={t('studio:explorer.incoming', { count: data.incoming.length })}
            icon={<ArrowDownLeft className="h-3.5 w-3.5 text-blue-600" aria-hidden />}
          >
            <NeighbourList items={data.incoming} onClick={onNeighbourClick} />
          </Section>
        )}
        {data.literals.length === 0 &&
          data.outgoing.length === 0 &&
          data.incoming.length === 0 && (
            <p className="text-slate-500">{t('common:status.empty')}</p>
          )}
        {data.truncated && (
          <p className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-small text-amber-800">
            {t('studio:explorer.truncated')}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function Section({
  title,
  icon,
  children,
}: {
  title: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h3 className="mb-1 flex items-center gap-1.5 text-h4 text-slate-700">
        {icon}
        {title}
      </h3>
      {children}
    </section>
  );
}

function NeighbourList({
  items,
  onClick,
}: {
  items: EntityNeighbour[];
  onClick: (iri: string) => void;
}) {
  return (
    <ul className="space-y-1">
      {items.map((n, idx) => (
        <li key={`${n.predicate_iri}-${n.target_iri}-${idx}`}>
          <button
            type="button"
            onClick={() => onClick(n.target_iri)}
            className={cn(
              'group w-full rounded border border-transparent px-2 py-1 text-left',
              'hover:border-slate-200 hover:bg-slate-50',
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="truncate font-medium text-brand-900 group-hover:text-accent">
                {n.target_label}
              </span>
              <span className="rounded bg-slate-100 px-1.5 text-small text-slate-500">
                {n.target_class}
              </span>
            </div>
            <p className="font-mono text-small text-slate-500">{n.predicate}</p>
          </button>
        </li>
      ))}
    </ul>
  );
}

function localName(iri: string): string {
  for (const sep of ['#', '/']) {
    if (iri.includes(sep)) return iri.split(sep).pop() || iri;
  }
  return iri;
}
